// Renders any LessonDoc (handbook original or AI adaptation). Also the print layout.
import type { LessonDoc } from "../../shared/lesson-model";
import { getPrinciple } from "../../shared/catalog";
import { Link } from "../lib/router";
import { StageStrip } from "./StageStrip";

export function LessonDocView({ doc, compact = false }: { doc: LessonDoc; compact?: boolean }) {
  return (
    <article className="doc">
      <header className="doc-head">
        <p className="doc-kicker">
          <span className="code">{doc.code}</span> {doc.topic}
        </p>
        <h1 className="doc-title">{doc.title}</h1>
        <p className="doc-meta">
          <span>{doc.level}</span>
          <span>{doc.duration} minutes</span>
          <span>{doc.skill}</span>
          {doc.source === "adapted" && <span className="tag-adapted">Adapted</span>}
        </p>
      </header>

      {doc.summary && <p className="doc-summary">{doc.summary}</p>}
      <StageStrip stages={doc.stages} size="lg" showLabels />

      <section className="doc-block doc-aims">
        <p><strong>Objective.</strong> {doc.objective}</p>
        {doc.successCheck && <p><strong>You'll know it worked when</strong> {doc.successCheck.replace(/^./, (c) => c.toLowerCase())}</p>}
        <p><strong>Materials.</strong> {doc.materials.join(" · ")}</p>
      </section>

      <ol className="stages">
        {doc.stages.map((s, i) => (
          <li key={i} className={`stage stage-${s.kind}`}>
            <div className="stage-when">
              <span className="stage-name">{s.name}</span>
              <span className="stage-min">{s.minutes} min</span>
              {s.interaction && <span className="stage-int">{s.interaction}</span>}
            </div>
            <div className="stage-what">
              <p>{s.teacher}</p>
              {s.students && <p className="stage-students"><span>Students</span> {s.students}</p>}
            </div>
          </li>
        ))}
      </ol>

      <section className="doc-block doc-diff">
        <div><h3>Support</h3><p>{doc.support}</p></div>
        {doc.stretch && <div><h3>Stretch</h3><p>{doc.stretch}</p></div>}
        <div><h3>Extension</h3><p>{doc.extension}</p></div>
        {doc.l1Bridge && <div><h3>L1 bridge</h3><p>{doc.l1Bridge}</p></div>}
      </section>

      {doc.teacherTalk && (
        <section className="doc-block">
          <h3>Teacher talk</h3>
          <ul className="talk">{doc.teacherTalk.map((t, i) => <li key={i}>{t}</li>)}</ul>
        </section>
      )}

      {!compact && doc.ledger && <Ledger items={doc.ledger} />}
      {!compact && doc.kit && <Kit kit={doc.kit} />}
    </article>
  );
}

export function Ledger({ items }: { items: NonNullable<LessonDoc["ledger"]> }) {
  return (
    <section className="doc-block ledger">
      <h3>What changed and why</h3>
      <ol>
        {items.map((l, i) => {
          const p = getPrinciple(l.principle);
          return (
            <li key={i}>
              <p className="ledger-change">{l.change}</p>
              <p className="ledger-why">{l.why}</p>
              {p && (
                <Link className="ledger-cite" to={`/handbook#p-${p.id.replace(".", "-")}`}>
                  Handbook §{p.id} · {p.title}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function Kit({ kit }: { kit: NonNullable<LessonDoc["kit"]> }) {
  return (
    <section className="doc-block kit">
      <h3>Materials kit</h3>
      <p className="muted small">Printed on its own pages. Cut along the dashed lines.</p>
      {kit.map((k, i) => (
        <div key={i} className={`kit-block kit-${k.kind}`}>
          <h4>{k.title}</h4>
          <ol className={k.kind === "cards" ? "kit-cards" : "kit-list"}>
            {k.items.map((it, j) => <li key={j}>{it}</li>)}
          </ol>
        </div>
      ))}
    </section>
  );
}
